import React, { useMemo, useState } from "react";
import { DEFAULT_PROJECT_EMOJI } from "@lifeat/shared/projects/ProjectsConstants";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import LifeAtCheckbox from "@common/LifeAtCheckbox/LifeAtCheckbox";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { projectList$ } from "../../projects/ProjectGlobals";
import { FilterTasksByProjectEvent } from "../AmplitudeTaskFilterEvents";
import { useTaskListContext } from "../TaskListContext";
import TagComponent from "./TagComponent";
import { TaskFilterButton } from "./TaskFilterButton";
import { DropdownItem } from "./TaskTagDropdownComponents";

const FILTER_OPTIONS_LABEL = "Display projects:";
const TRIGGER_LABEL = "Projects";
const ALL_OPTION_LABEL = "All";

interface TaskProjectFilterDropdownProps {
    readonly amplitudeEventSource: AmplitudeEventSource;
}
export function TaskProjectFilterDropdown(props: TaskProjectFilterDropdownProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { filters, toggleProjectFilter, clearProjectFilters } = useTaskListContext();
    const { userData } = useUserContext();
    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();

    const isProUser = userData?.proUserInfo?.isProUser;

    const selectedProjectIdSet = useMemo(() => {
        const ret = new Set<string>();
        for (const filter of filters) {
            if (filter.type === "project") {
                ret.add(filter.project);
            }
        }
        return ret;
    }, [filters]);

    return (
        <DropdownMenuComponents.Root onOpenChange={(isOpen: boolean) => setIsDropdownOpen(isOpen)}>
            <DropdownMenuComponents.Trigger>
                <TaskFilterButton
                    label={TRIGGER_LABEL}
                    count={filters.filter((filter) => filter.type === "project").length}
                    isOpen={isDropdownOpen}
                />
            </DropdownMenuComponents.Trigger>
            <DropdownMenuComponents.Content>
                <DropdownMenuComponents.HeaderItem label={FILTER_OPTIONS_LABEL} />
                <DropdownItem onClick={() => clearProjectFilters()}>
                    <LifeAtCheckbox checked={selectedProjectIdSet.size === 0} />
                    {ALL_OPTION_LABEL}
                </DropdownItem>

                {Object.entries(projectList$.value).map(([projectId, project]) => (
                    <DropdownItem
                        key={projectId}
                        onClick={() => {
                            if (!isProUser) {
                                setUpgradeCtaContext("projects", UpgradeCTASource.PROJECTS);
                            } else {
                                toggleProjectFilter(projectId);
                                amplitudeService.logEvent(
                                    new FilterTasksByProjectEvent({
                                        source: props.amplitudeEventSource,
                                        selectedProjectsCount: selectedProjectIdSet.size,
                                    }),
                                );
                            }
                        }}
                        isDisabled={!isProUser}
                    >
                        <LifeAtCheckbox checked={selectedProjectIdSet.has(projectId)} />
                        <TagComponent
                            tag={{
                                name: project.title,
                                emoji: project.emoji ?? DEFAULT_PROJECT_EMOJI,
                                color: project.color,
                            }}
                            disableHover={true}
                        />
                    </DropdownItem>
                ))}
            </DropdownMenuComponents.Content>
        </DropdownMenuComponents.Root>
    );
}
